<template>
  <div>
    <AppLayout title="Planos">
      <template #actions>
        <button type="button" class="btn btn-primary" @click="openNew"><i class="bi bi-plus-lg me-1"></i> Novo Plano</button>
      </template>

      <div class="table-card">
        <table class="table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Preço</th>
              <th>Limite de produtos</th>
              <th>Limite de operadores</th>
              <th>Limite de lojas</th>
              <th>Clientes</th>
              <th>Status</th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="plan in plansList" :key="plan.id">
              <td>{{ plan.name }}</td>
              <td>{{ formatter.currency(plan.price) }}</td>
              <td>{{ plan.max_products ?? 'Ilimitado' }}</td>
              <td>{{ plan.max_operators ?? 'Ilimitado' }}</td>
              <td>{{ plan.max_stores ?? 'Ilimitado' }}</td>
              <td>{{ plan.users_count }}</td>
              <td>
                <span class="badge" :class="plan.active ? 'text-bg-success' : 'text-bg-secondary'">
                  {{ plan.active ? 'Ativo' : 'Inativo' }}
                </span>
              </td>
              <td style="width:44px"><button @click="openEdit(plan)" type="button" class="btn btn-outline-secondary btn-sm"><i class="bi bi-pencil-square"></i></button></td>
              <td style="width:44px"><button @click="openDelete(plan)" type="button" class="btn btn-outline-danger btn-sm"><i class="bi bi-trash-fill"></i></button></td>
            </tr>
            <tr v-if="!plansList.length">
              <td colspan="9" class="text-center text-muted">Nenhum plano cadastrado ainda.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </AppLayout>

    <!-- Modal Plano (novo/editar) -->
    <div class="modal fade" id="modal_plan_form" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editMode ? 'Editar Plano' : 'Novo Plano' }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <form @submit.prevent="submitPlan">
            <div class="modal-body">
              <div v-if="formError" class="alert alert-danger py-2 small">{{ formError }}</div>

              <div class="mb-3">
                <label class="form-label">Nome:</label>
                <input v-model="form.name" @input="syncSlug" required type="text" class="form-control">
              </div>
              <div class="mb-3">
                <label class="form-label">Identificador (slug):</label>
                <input v-model="form.slug" required type="text" class="form-control">
              </div>
              <div class="row">
                <div class="mb-3 col">
                  <label class="form-label">Preço mensal (R$):</label>
                  <input v-model="form.price" required type="number" step="0.01" min="0" class="form-control">
                </div>
              </div>
              <div class="row">
                <div class="mb-3 col">
                  <label class="form-label">Limite de produtos:</label>
                  <input v-model="form.max_products" type="number" min="0" class="form-control" placeholder="Ilimitado">
                </div>
                <div class="mb-3 col">
                  <label class="form-label">Limite de operadores:</label>
                  <input v-model="form.max_operators" type="number" min="0" class="form-control" placeholder="Ilimitado">
                </div>
                <div class="mb-3 col">
                  <label class="form-label">Limite de lojas:</label>
                  <input v-model="form.max_stores" type="number" min="0" class="form-control" placeholder="Ilimitado">
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label">Descrição:</label>
                <textarea v-model="form.description" class="form-control" rows="2"></textarea>
              </div>
              <div class="form-check">
                <input v-model="form.active" class="form-check-input" type="checkbox" id="plan_active">
                <label class="form-check-label" for="plan_active">Plano ativo (disponível para atribuir a clientes)</label>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
              <button type="submit" class="btn btn-primary" :disabled="saving">{{ saving ? 'Salvando...' : 'Salvar' }}</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Modal Deletar -->
    <div class="modal fade" id="modal_plan_delete" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Excluir plano?</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="formError" class="alert alert-danger py-2 small">{{ formError }}</div>
            <p>{{ selectedPlan?.name }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
            <button type="button" @click="confirmDelete" class="btn btn-danger" :disabled="saving">Excluir</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, reactive, ref } from 'vue';
import AppLayout from "@/components/AppLayout.vue";
import { Modal } from "bootstrap";
import { plans } from "@/models/plans";
import formatter from "@/services/formatter";

const emptyForm = () => ({
  id: null,
  name: '',
  slug: '',
  price: '',
  max_products: '',
  max_operators: '',
  max_stores: '',
  description: '',
  active: true,
});

export default {
  name: 'PlansView',
  components: { AppLayout },
  setup() {
    const Plans = plans();

    const plansList = ref([]);
    const editMode = ref(false);
    const saving = ref(false);
    const formError = ref('');
    const selectedPlan = ref(null);
    const form = reactive(emptyForm());

    let modals = {};

    function openModal(id) {
      modals[id] = modals[id] ?? new Modal(document.getElementById(id));
      modals[id].show();
    }

    function closeModal(id) {
      modals[id]?.hide();
    }

    async function fetchPlans() {
      plansList.value = await Plans.list();
    }

    // Sugere o slug a partir do nome só na criação — editar o nome de um
    // plano existente não deve mudar seu identificador por baixo.
    function syncSlug() {
      if (editMode.value) return;
      form.slug = form.name
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
    }

    function openNew() {
      Object.assign(form, emptyForm());
      formError.value = '';
      editMode.value = false;
      openModal('modal_plan_form');
    }

    function openEdit(plan) {
      Object.assign(form, {
        id: plan.id,
        name: plan.name,
        slug: plan.slug,
        price: plan.price,
        max_products: plan.max_products ?? '',
        max_operators: plan.max_operators ?? '',
        max_stores: plan.max_stores ?? '',
        description: plan.description ?? '',
        active: plan.active,
      });
      formError.value = '';
      editMode.value = true;
      openModal('modal_plan_form');
    }

    async function submitPlan() {
      saving.value = true;
      formError.value = '';
      const payload = {
        name: form.name,
        slug: form.slug,
        price: form.price,
        max_products: form.max_products === '' ? null : form.max_products,
        max_operators: form.max_operators === '' ? null : form.max_operators,
        max_stores: form.max_stores === '' ? null : form.max_stores,
        description: form.description || null,
        active: form.active,
      };
      try {
        if (editMode.value) {
          await Plans.update(form.id, payload);
        } else {
          await Plans.create(payload);
        }
        closeModal('modal_plan_form');
        await fetchPlans();
      } catch (err) {
        formError.value = firstErrorMessage(err);
      } finally {
        saving.value = false;
      }
    }

    function openDelete(plan) {
      selectedPlan.value = plan;
      formError.value = '';
      openModal('modal_plan_delete');
    }

    async function confirmDelete() {
      saving.value = true;
      formError.value = '';
      try {
        await Plans.remove(selectedPlan.value.id);
        closeModal('modal_plan_delete');
        await fetchPlans();
      } catch (err) {
        formError.value = firstErrorMessage(err);
      } finally {
        saving.value = false;
      }
    }

    function firstErrorMessage(err) {
      const firstError = Object.values(err?.errors ?? {})[0];
      return firstError?.[0] ?? err?.message ?? 'Não foi possível concluir a ação.';
    }

    onMounted(fetchPlans);

    return {
      plansList, editMode, saving, formError, selectedPlan, form, formatter,
      openNew, openEdit, submitPlan, openDelete, confirmDelete, syncSlug,
    };
  },
}
</script>
